/**
 * Phase 1.7 — Tools module index, v2.
 *
 * Cards are interactive and navigate to /tools/:id; the view toggle
 * (grid / list) actually re-renders.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit2, Plus, Trash2, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, EmptyState, FilterBar, KpiCard, SectionHeader, ViewToggle, type ViewMode } from '@/components/v2';
import { authFetch } from '@/lib/auth';
import { useUiPrefs } from '@/lib/layout-flag';
import { usePageHeader } from '@/components/v2/layout/Header';
import { AddEntityDialog } from '@/components/v2/dialogs/AddEntityDialog';
import { EditEntityDialog } from '@/components/v2/dialogs/EditEntityDialog';
import { DeleteEntityDialog } from '@/components/v2/dialogs/DeleteEntityDialog';
import { EntityImage } from '@/components/EntityImage';

type ConditionStatus = 'serviceable' | 'needs_repair' | 'out_of_service' | 'retired';

interface ToolRow {
  id: string;
  ranch_id: string;
  name: string;
  asset_tag?: string | null;
  model_number?: string | null;
  serial_number?: string | null;
  condition_status?: ConditionStatus | null;
  type?: string | null;
  condition?: string | null;
  location?: string | null;
  notes?: string | null;
}

const STATUS_ORDER: ConditionStatus[] = ['serviceable', 'needs_repair', 'out_of_service', 'retired'];
const STATUS_LABEL: Record<ConditionStatus, string> = {
  serviceable: 'Serviceable',
  needs_repair: 'Needs Repair',
  out_of_service: 'Out of Service',
  retired: 'Retired',
};
const STATUS_TONE: Record<ConditionStatus, string> = {
  serviceable: 'bg-moss/10 text-moss dark:bg-moss-dark/20 dark:text-moss-dark',
  needs_repair: 'bg-clay/10 text-clay dark:bg-clay-dark/20 dark:text-clay-dark',
  out_of_service: 'bg-charcoal/10 text-charcoal dark:bg-charcoal-dark/20 dark:text-charcoal-dark',
  retired: 'bg-charcoal/10 text-charcoal dark:bg-charcoal-dark/20 dark:text-charcoal-dark',
};

const VIEW_KEY = 'tools';

const FIELDS = [
  { name: 'name', label: 'Name', required: true, placeholder: 'Cordless drill' },
  { name: 'asset_tag', label: 'Asset tag', half: true, placeholder: 'TOOL-001' },
  {
    name: 'condition_status',
    label: 'Condition',
    type: 'select' as const,
    half: true,
    defaultValue: 'serviceable',
    options: STATUS_ORDER.map((s) => ({ value: s, label: STATUS_LABEL[s] })),
  },
  { name: 'model_number', label: 'Model #', half: true },
  { name: 'serial_number', label: 'Serial #', half: true },
  { name: 'notes', label: 'Notes', type: 'textarea' as const },
];

const transformTool = (v: Record<string, string>): Record<string, unknown> => ({
  ...(v.name !== undefined ? { name: v.name } : {}),
  ...(v.asset_tag !== undefined ? { asset_tag: v.asset_tag || null } : {}),
  ...(v.condition_status !== undefined ? { condition_status: v.condition_status || 'serviceable' } : {}),
  ...(v.model_number !== undefined ? { model_number: v.model_number || null } : {}),
  ...(v.serial_number !== undefined ? { serial_number: v.serial_number || null } : {}),
  ...(v.notes !== undefined ? { notes: v.notes || null } : {}),
});

function statusOf(t: ToolRow): ConditionStatus {
  return t.condition_status ?? 'serviceable';
}

export function ToolsPageV2(): JSX.Element {
  const navigate = useNavigate();
  const { prefs, patch } = useUiPrefs();
  const view: ViewMode = prefs.view_modes?.[VIEW_KEY] ?? 'grid';
  const [tools, setTools] = useState<ToolRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | ConditionStatus>('all');
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [editTarget, setEditTarget] = useState<ToolRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ToolRow | null>(null);
  const [refreshTick, setRefreshTick] = useState<number>(0);

  usePageHeader(
    'Tools',
    <Button
      size="sm"
      onClick={() => setAddOpen(true)}
      className="bg-burnt-orange text-white hover:bg-burnt-orange/90 dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90"
    >
      <Plus className="h-4 w-4" />
      Add tool
    </Button>,
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    async function load(): Promise<void> {
      try {
        const data = await authFetch<{ items: ToolRow[] }>('/api/tools');
        if (!cancelled) setTools(data.items ?? []);
      } catch (err) {
        if (!cancelled) setError((err as Error).message ?? 'Failed to load tools');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [refreshTick]);

  const filtered = useMemo<ToolRow[]>(() => {
    const q = search.trim().toLowerCase();
    return tools.filter((t) => {
      if (q && !`${t.name} ${t.asset_tag ?? ''} ${t.model_number ?? ''}`.toLowerCase().includes(q)) return false;
      if (statusFilter !== 'all' && statusOf(t) !== statusFilter) return false;
      return true;
    });
  }, [tools, search, statusFilter]);

  const grouped = useMemo<Record<ConditionStatus, ToolRow[]>>(() => {
    const out = STATUS_ORDER.reduce<Record<ConditionStatus, ToolRow[]>>(
      (acc, s) => ({ ...acc, [s]: [] }),
      {} as Record<ConditionStatus, ToolRow[]>,
    );
    for (const t of filtered) out[statusOf(t)].push(t);
    return out;
  }, [filtered]);

  const totals = useMemo(() => {
    const serviceable = tools.filter((t) => statusOf(t) === 'serviceable').length;
    const needsAttention = tools.length - serviceable;
    return { serviceable, needsAttention };
  }, [tools]);

  const setView = (next: ViewMode): void => {
    void patch({ view_modes: { ...(prefs.view_modes ?? {}), [VIEW_KEY]: next } });
  };

  const hasActiveFilters = Boolean(search) || statusFilter !== 'all';
  const clear = (): void => { setSearch(''); setStatusFilter('all'); };
  const refresh = (): void => setRefreshTick((t) => t + 1);
  const stop = (e: React.MouseEvent): void => e.stopPropagation();

  return (
    <div className="space-y-6">
      <AddEntityDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        title="Add Tool"
        endpoint="/api/tools"
        entityType="tool"
        fields={FIELDS}
        transform={transformTool}
        onAdded={refresh}
      />
      {editTarget ? (
        <EditEntityDialog
          open={Boolean(editTarget)}
          onOpenChange={(o) => { if (!o) setEditTarget(null); }}
          title={`Edit ${editTarget.name}`}
          endpoint="/api/tools"
          entityId={editTarget.id}
          fields={FIELDS}
          initialValues={{
            name: editTarget.name,
            asset_tag: editTarget.asset_tag ?? '',
            condition_status: statusOf(editTarget),
            model_number: editTarget.model_number ?? '',
            serial_number: editTarget.serial_number ?? '',
            notes: editTarget.notes ?? '',
          }}
          transform={transformTool}
          onSaved={refresh}
        />
      ) : null}
      {deleteTarget ? (
        <DeleteEntityDialog
          open={Boolean(deleteTarget)}
          onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
          endpoint="/api/tools"
          entityId={deleteTarget.id}
          entityName={deleteTarget.name}
          onDeleted={refresh}
        />
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <KpiCard label="Tools" icon={Wrench} primary={{ label: 'Serviceable', value: totals.serviceable }} secondary={{ label: 'Needs attention', value: totals.needsAttention, tone: totals.needsAttention > 0 ? 'warning' : 'default' }} />
        <KpiCard label="Filter" primary={{ label: 'Showing', value: filtered.length }} secondary={{ label: 'Total', value: tools.length }} />
      </div>

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: 'Search tools' }}
        filters={[
          <FilterSelect
            key="status"
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as 'all' | ConditionStatus)}
            options={[{ value: 'all', label: 'All conditions' }, ...STATUS_ORDER.map((s) => ({ value: s, label: STATUS_LABEL[s] }))]}
          />,
        ]}
        actions={<ViewToggle value={view} onChange={setView} />}
        onClear={clear}
        hasActiveFilters={hasActiveFilters}
      />

      {error ? <div className="rounded-lg bg-clay/10 p-4 text-sm text-clay dark:bg-clay-dark/20 dark:text-clay-dark">{error}</div> : null}

      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Wrench}
          title={hasActiveFilters ? 'No matches' : 'No tools tracked yet'}
          description={hasActiveFilters ? 'Try clearing filters.' : 'Add a tool to track condition and storage.'}
          action={hasActiveFilters ? <Button onClick={clear} variant="outline">Clear filters</Button> : <Button onClick={() => setAddOpen(true)}><Plus className="h-4 w-4" />Add tool</Button>}
        />
      ) : (
        STATUS_ORDER.filter((s) => grouped[s].length > 0).map((status) => (
          <SectionHeader key={status} title={STATUS_LABEL[status]} count={grouped[status].length} collapsible>
            {view === 'grid' ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {grouped[status].map((t) => (
                  <Card
                    key={t.id}
                    interactive
                    onClick={() => navigate(`/tools/${t.id}`)}
                    thumbnail={<EntityImage entityType="tool" entityId={t.id} alt={t.name} />}
                    title={t.name}
                    subtitle={t.asset_tag || t.model_number || 'Tool'}
                    badge={
                      <span className={`rounded-full px-2 py-0.5 font-mono text-2xs uppercase tracking-wider ${STATUS_TONE[statusOf(t)]}`}>
                        {STATUS_LABEL[statusOf(t)]}
                      </span>
                    }
                    footer={
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={(e) => { stop(e); setEditTarget(t); }} aria-label={`Edit ${t.name}`} className="h-7 w-7">
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={(e) => { stop(e); setDeleteTarget(t); }} aria-label={`Archive ${t.name}`} className="h-7 w-7 text-destructive hover:text-destructive">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    }
                  />
                ))}
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-border bg-surface dark:bg-surface-dark dark:border-border">
                <table className="w-full text-sm">
                  <thead className="border-b border-border bg-surface-raised text-left dark:bg-surface-raised-dark">
                    <tr>
                      <Th>Name</Th>
                      <Th>Asset tag</Th>
                      <Th>Model</Th>
                      <Th>Condition</Th>
                      <Th className="text-right">Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped[status].map((t) => (
                      <tr
                        key={t.id}
                        className="cursor-pointer border-b border-border last:border-0 hover:bg-surface-raised dark:hover:bg-surface-raised-dark"
                        onClick={() => navigate(`/tools/${t.id}`)}
                      >
                        <Td className="font-medium">{t.name}</Td>
                        <Td className="font-mono text-xs">{t.asset_tag || '\u2014'}</Td>
                        <Td className="text-muted-foreground">{t.model_number || '\u2014'}</Td>
                        <Td>
                          <span className={`inline-flex rounded-full px-2 py-0.5 font-mono text-2xs uppercase tracking-wider ${STATUS_TONE[statusOf(t)]}`}>
                            {STATUS_LABEL[statusOf(t)]}
                          </span>
                        </Td>
                        <Td className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={(e) => { stop(e); setEditTarget(t); }} aria-label={`Edit ${t.name}`} className="h-7 w-7">
                              <Edit2 className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={(e) => { stop(e); setDeleteTarget(t); }} aria-label={`Archive ${t.name}`} className="h-7 w-7 text-destructive hover:text-destructive">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionHeader>
        ))
      )}
    </div>
  );
}

function FilterSelect({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }): JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-md border border-border bg-surface px-2 text-sm shadow-sm dark:bg-surface-dark dark:border-border"
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }): JSX.Element {
  return <th className={`px-4 py-2 font-mono text-2xs uppercase tracking-wider text-muted-foreground ${className ?? ''}`}>{children}</th>;
}
function Td({ children, className }: { children: React.ReactNode; className?: string }): JSX.Element {
  return <td className={`px-4 py-3 ${className ?? ''}`}>{children}</td>;
}
